import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  X, 
  FileText,
  Calendar,
  DollarSign,
  Users,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  FileCheck,
  Shield,
  Settings
} from 'lucide-react';
import ContractService from '../../services/contractService';
import AssetService from '../../services/assetService';
import MasterDataService from '../../services/masterDataService';
import VendorService from '../../services/vendorService';
import CustomerService from '../../services/customerService';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ContractEdit = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [assets, setAssets] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [contractStatuses, setContractStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [counterparties, setCounterparties] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    counterparty: '',
    counterpartyContact: {
      name: '',
      email: '',
      phone: '',
      address: ''
    },
    startDate: '',
    endDate: '',
    status: '',
    amount: '',
    currency: 'USD',
    paymentTerms: '',
    frequency: '',
    autoRenewal: false,
    renewalTerms: '',
    terminationClause: '',
    noticePeriod: 30,
    linkedAssets: [],
    description: '',
    terms: '',
    conditions: '',
    tags: [],
    customFields: {},
    department: '',
    location: ''
  });

  // Load contract data and dropdown options
  useEffect(() => {
    loadContractData();
    loadDropdownData();
  }, [contractId]);

  const loadContractData = async () => {
    try {
      setLoading(true);
      //console.log('Loading contract:', contractId);
      
      const response = await ContractService.getContractById(contractId);
      const contractData = response.data;
      
      //console.log('Contract data loaded:', contractData);
      
      setContract(contractData);
      setFormData({
        title: contractData.title || '',
        type: contractData.type || '',
        counterparty: contractData.counterparty || '',
        counterpartyContact: contractData.counterpartyContact || {
          name: '',
          email: '',
          phone: '',
          address: ''
        },
        startDate: contractData.startDate ? new Date(contractData.startDate).toISOString().split('T')[0] : '',
        endDate: contractData.endDate ? new Date(contractData.endDate).toISOString().split('T')[0] : '',
        status: contractData.status || '',
        amount: contractData.amount || '',
        currency: contractData.currency || 'USD',
        paymentTerms: contractData.paymentTerms || '',
        frequency: contractData.frequency || '',
        autoRenewal: contractData.autoRenewal || false,
        renewalTerms: contractData.renewalTerms || '',
        terminationClause: contractData.terminationClause || '',
        noticePeriod: contractData.noticePeriod || 30,
        linkedAssets: Array.isArray(contractData.linkedAssets) ? contractData.linkedAssets : [],
        description: contractData.description || '',
        terms: contractData.terms || '',
        conditions: contractData.conditions || '',
        tags: contractData.tags || [],
        customFields: contractData.customFields || {}
      });
    } catch (error) {
      console.error('Error loading contract:', error);
      
      // Handle 404 errors more gracefully
      if (error.response?.status === 404) {
        setError(`Contract with ID "${contractId}" not found. This might be a test contract that only exists in the frontend. To edit contracts, they need to be saved to the backend database first.`);
      } else {
        setError('Failed to load contract data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [masterDataRes, assetsRes, vendorsRes, customersRes] = await Promise.all([
        MasterDataService.getContractDropdowns(),
        AssetService.getAssets({ limit: 100 }).catch(err => {
          console.warn('Assets service not available, using empty array:', err);
          return { data: [] };
        }),
        VendorService.getVendors({ limit: 1000 }).catch(err => {
          console.warn('Vendors service not available, using empty array:', err);
          return { data: [] };
        }),
        CustomerService.getCustomers({ limit: 1000 }).catch(err => {
          console.warn('Customers service not available, using empty array:', err);
          return { data: [] };
        })
      ]);

      const dropdownData = masterDataRes.data;
      
      // Transform master data to dropdown format
      setContractTypes(dropdownData.ContractType?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setContractStatuses(dropdownData.Status?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setPriorities(dropdownData.Priority?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setCurrencies(dropdownData.Currency?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setPaymentTerms(dropdownData.PaymentTerms?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setFrequencies(dropdownData.Frequency?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setDepartments(dropdownData.Department?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setLocations(dropdownData.Location?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      
      // Combine vendors and customers for counterparty dropdown
      const vendors = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];
      const customers = Array.isArray(customersRes.data) ? customersRes.data : [];
      const allCounterparties = [
        ...vendors.map(v => ({ value: v.name || v.vendorName, label: `${v.name || v.vendorName} (Vendor)` })),
        ...customers.map(c => ({ value: c.name || c.customerName, label: `${c.name || c.customerName} (Customer)` }))
      ];
      setCounterparties(allCounterparties);
      
      //console.log('Loaded master data dropdowns:', dropdownData);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      // Set fallback empty arrays
      setContractTypes([]);
      setContractStatuses([]);
      setPriorities([]);
      setCurrencies([]);
      setPaymentTerms([]);
      setFrequencies([]);
      setDepartments([]);
      setLocations([]);
      setAssets([]);
      setCounterparties([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleAssetSelection = (assetId, isSelected) => {
    setFormData(prev => ({
      ...prev,
      linkedAssets: isSelected 
        ? [...(prev.linkedAssets || []), assetId]
        : (prev.linkedAssets || []).filter(id => id !== assetId)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      //console.log('Saving contract:', formData);
      
      // Validate required fields
      if (!formData.title || !formData.type || !formData.counterparty || !formData.startDate || !formData.endDate || !formData.amount) {
        showError('Please fill in all required fields');
        return;
      }
      
      // Convert dates to ISO string format for backend and ensure type is preserved
      const contractData = {
        ...formData,
        // Ensure type is trimmed and preserved
        type: formData.type ? String(formData.type).trim() : formData.type,
        title: formData.title ? String(formData.title).trim() : formData.title,
        counterparty: formData.counterparty ? String(formData.counterparty).trim() : formData.counterparty,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : new Date().toISOString(),
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        noticePeriod: formData.noticePeriod ? parseInt(formData.noticePeriod) || 30 : 30
      };
      
      // Remove fields that shouldn't be updated
      delete contractData.id;
      delete contractData.contractId;
      delete contractData.company;
      delete contractData.createdBy;
      delete contractData.createdAt;
      
      // Debug log to verify type is being sent
      console.log('Updating contract with type:', contractData.type);
      
      //console.log('Sending contract data to API:', contractData);
      
      const response = await ContractService.updateContract(contractId, contractData);
      
      //console.log('Contract update response:', response);
      
      if (response?.success !== false) {
        success('Contract updated successfully!');
        setTimeout(() => {
          navigate('/contracts/management');
        }, 1500);
      } else {
        showError(response.message || 'Failed to update contract');
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save contract';
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/contracts/management');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'var(--font-family)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--border-color)',
            borderTop: '4px solid #4678be',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '16px', fontFamily: 'var(--font-family)' }}>Loading contract...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'var(--font-family)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'var(--font-family)' }}>Error Loading Contract</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontFamily: 'var(--font-family)' }}>{error}</p>
          <button
            onClick={handleCancel}
            className="btn btn-primary"
            style={{
              padding: '12px 24px',
              backgroundColor: '#4678be',
              color: 'white',
              border: 'none',
              borderRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'var(--font-family)'
            }}
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={formData.title || "Edit Contract"}
        description="Update contract information and settings"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} style={{ width: '100%' }}>
            {/* Tab Content - Using project-form-card for consistency */}
            <div className="project-form-card">
              {/* Form Content */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px'
              }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Basic Information */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <FileText size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Basic Information
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Contract Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="Enter contract title"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Contract Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Type</option>
                  {contractTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Counterparty *
                </label>
                <select
                  value={formData.counterparty}
                  onChange={(e) => handleInputChange('counterparty', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Counterparty</option>
                  {counterparties.map(cp => (
                    <option key={cp.value} value={cp.value}>
                      {cp.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Status</option>
                  {contractStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Priority *
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Priority</option>
                  {priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Dates & Duration */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Calendar size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Dates & Duration
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Notice Period (Days)
                </label>
                <input
                  type="number"
                  value={formData.noticePeriod}
                  onChange={(e) => handleInputChange('noticePeriod', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="30"
                />
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
                <input
                  type="checkbox"
                  id="autoRenewal"
                  checked={formData.autoRenewal}
                  onChange={(e) => handleInputChange('autoRenewal', e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <label htmlFor="autoRenewal" style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  Auto Renewal
                </label>
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <DollarSign size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Financial Details
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Amount *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Currency</option>
                  {currencies.map(currency => (
                    <option key={currency.value} value={currency.value}>
                      {currency.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Payment Terms
                </label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Payment Terms</option>
                  {paymentTerms.map(term => (
                    <option key={term.value} value={term.value}>
                      {term.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => handleInputChange('frequency', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Frequency</option>
                  {frequencies.map(frequency => (
                    <option key={frequency.value} value={frequency.value}>
                      {frequency.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Department & Location */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Building size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Department & Location
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Department
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Department</option>
                  {departments.map(department => (
                    <option key={department.value} value={department.value}>
                      {department.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Location
                </label>
                <select
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Location</option>
                  {locations.map(location => (
                    <option key={location.value} value={location.value}>
                      {location.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Counterparty Contact */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Users size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Counterparty Contact
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.counterpartyContact.name}
                  onChange={(e) => handleNestedInputChange('counterpartyContact', 'name', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter contact name"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.counterpartyContact.email}
                  onChange={(e) => handleNestedInputChange('counterpartyContact', 'email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.counterpartyContact.phone}
                  onChange={(e) => handleNestedInputChange('counterpartyContact', 'phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter phone number"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Address
                </label>
                <textarea
                  value={formData.counterpartyContact.address}
                  onChange={(e) => handleNestedInputChange('counterpartyContact', 'address', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter address"
                />
              </div>
            </div>
          </div>

          {/* Linked Assets */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Building size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Linked Assets
                </h3>
              </div>
            </div>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {Array.isArray(assets) && assets.length > 0 ? (
                assets.map(asset => (
                  <div key={asset.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="checkbox"
                      checked={(formData.linkedAssets || []).includes(asset.id)}
                      onChange={(e) => handleAssetSelection(asset.id, e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'var(--text-primary)',
                      fontFamily: 'var(--font-family)'
                      }}>
                        {asset.name}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        fontFamily: 'var(--font-family)'
                      }}>
                        {asset.type} • {asset.status}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)'
                }}>
                  No assets available
                </div>
              )}
            </div>
          </div>

          {/* Terms & Conditions */}
          <div className="project-form-section">
            <div className="section-header">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <FileCheck size={20} style={{ color: '#4678be' }} />
                <h3 className="section-title" style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Terms & Conditions
                </h3>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter contract description"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Terms
                </label>
                <textarea
                  value={formData.terms}
                  onChange={(e) => handleInputChange('terms', e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter contract terms"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Conditions
                </label>
                <textarea
                  value={formData.conditions}
                  onChange={(e) => handleInputChange('conditions', e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter contract conditions"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Termination Clause
                </label>
                <textarea
                  value={formData.terminationClause}
                  onChange={(e) => handleInputChange('terminationClause', e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter termination clause"
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Renewal Terms
                </label>
                <textarea
                  value={formData.renewalTerms}
                  onChange={(e) => handleInputChange('renewalTerms', e.target.value)}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Enter renewal terms"
                />
              </div>
              </div>
            </div>
          </div>
        </div>
            </div>

            {/* Form Actions */}
            <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', padding: '24px 0', borderTop: '1px solid var(--border-color)', marginTop: '32px' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  borderRadius: '0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'var(--font-family)',
                  minWidth: '120px'
                }}
              >
                <X size={18} />
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: saving ? '#9ca3af' : '#4678be',
                  border: 'none',
                  color: 'white',
                  borderRadius: '0',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'var(--font-family)',
                  minWidth: '120px'
                }}
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ContractEdit;
